// «نمایش صفحه‌ی من به پشتیبانی» (60.11.559, backend 1.0.0.708, CRM 8.4.1.0.227): the customer's screen, live, for support -
// no control, nothing recorded. One share at a time for the whole panel: it goes on while the customer moves between the
// panel's pages to show the problem, until «توقف», the browser's own «Stop sharing», or the page closing. Since 60.11.580
// it runs in the capture window (capture-window.ts), so a refresh of the panel does not end it; the panel's tabs mirror it.
// The panel is the sharer: it talks to the CRM's door (megapay_screen_share.php) with the share's own token, offers its
// screen to the colleague the CRM announces, and the picture goes browser to browser (or through turn.asatsp.ir).
import {startTicketScreenShare} from '../support-ticket-api'
import {steadyTimeout} from './steady-timer'
import {CAPTURE_WINDOW_WAIT_MS, inCaptureWindow, openCaptureWindow} from './capture-window'

export type ScreenShareStart = {
  id: number
  token: string
  endpoint: string
  pollMs: number
  iceServers: RTCIceServer[]
}

/** «choosing»: the capture window waits for the customer to choose the screen (60.11.580). */
export type ScreenShareStatus = 'idle' | 'choosing' | 'starting' | 'waiting' | 'viewing' | 'ended'

export type ScreenShareState = {
  status: ScreenShareStatus
  ticketNumber?: string
  /** The colleague watching now. */
  viewer?: string | null
  /** When the share began (ms). */
  since?: number
  /** Why it ended, when not by the customer. */
  problem?: string
}

type Signal = {id: number; kind: string; payload: string}
type PollResult = {
  state: 'waiting' | 'viewing' | 'ended'
  viewer?: string | null
  signals: Signal[]
}
type Tunable = RTCRtpSendParameters & {degradationPreference?: string}

/** The share went away at the CRM (ended, or never known). */
class ShareGone extends Error {}

/** How quickly the panel asks while a colleague's picture is being set up, and the most a screen share is sent at. */
export const NEGOTIATING_POLL_MS = 400
export const MAX_BITRATE = 2_500_000

const listeners = new Set<(state: ScreenShareState) => void>()
let current: ScreenShareState = {status: 'idle'}
let session: ScreenShareSession | null = null
let endedTimer = 0
/** This page is asking the browser for a screen of its own. */
let localStarting = false
/** The share the capture window holds, as a panel tab sees it, and how to stop it (capture-hub.ts). */
let remote: {state: ScreenShareState; stop: () => void} | null = null
let waitTimer = 0

const publish = (next: ScreenShareState) => {
  current = next
  listeners.forEach((listener) => listener(current))
}

export const getScreenShare = () => current

export const subscribeScreenShare = (listener: (state: ScreenShareState) => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Whether a share is asked for, starting or on. */
export const screenShareBusy = (state: ScreenShareState = current) =>
  state.status === 'choosing' ||
  state.status === 'starting' ||
  state.status === 'waiting' ||
  state.status === 'viewing'

/**
 * A panel tab beside a capture window: the share the window holds (null: none any more) and how to stop it. A share of
 * this page's own goes first.
 */
export const mirrorScreenShare = (
  state: ScreenShareState | null,
  stop: () => void = () => undefined
) => {
  const next = state && state.status !== 'idle' ? state : null
  if (!next && !remote) return
  remote = next ? {state: next, stop} : null
  if (session || localStarting) return
  window.clearTimeout(waitTimer)
  publish(next || {status: 'idle'})
}

/** In the capture window: the customer is to choose the screen to show for the ticket. */
export const prepareScreenShare = (ticketNumber: string) => {
  if (session || localStarting) return
  window.clearTimeout(endedTimer)
  publish({status: 'choosing', ticketNumber})
}

/** Whether this browser shares a screen: a computer's, not a phone's or a tablet's. */
export const canShareScreen = () =>
  typeof window !== 'undefined' &&
  typeof window.RTCPeerConnection !== 'undefined' &&
  !!navigator.mediaDevices?.getDisplayMedia &&
  !window.matchMedia?.('(pointer: coarse)').matches

export class ScreenShareSession {
  private pc: RTCPeerConnection | null = null
  private after = 0
  private queued: RTCIceCandidateInit[] = []
  private cancelPoll = () => {}
  private connected = false
  private stopped = false

  constructor(
    private readonly start: ScreenShareStart,
    private readonly stream: MediaStream,
    private readonly ticketNumber: string
  ) {}

  begin() {
    this.stream.getVideoTracks().forEach((track) => track.addEventListener('ended', this.onEnded))
    window.addEventListener('pagehide', this.onPageHide)
    publish({status: 'waiting', ticketNumber: this.ticketNumber, since: Date.now(), viewer: null})
    void this.poll()
  }

  /** «توقف»: the CRM is told (and keeps the ticket's note), the screen is let go. */
  stop() {
    if (this.stopped) return
    void fetch(this.url('&action=end'), {
      method: 'POST',
      headers: {'X-Asa-Share-Token': this.start.token},
      keepalive: true,
    }).catch(() => undefined)
    this.finish()
  }

  private url(extra: string) {
    return `${this.start.endpoint}?share=${this.start.id}${extra}`
  }

  private async call<T>(method: 'GET' | 'POST', extra: string, body?: unknown): Promise<T> {
    const response = await fetch(this.url(extra), {
      method,
      headers: {
        'X-Asa-Share-Token': this.start.token,
        ...(body === undefined ? {} : {'Content-Type': 'application/json'}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
    if (response.status === 404) throw new ShareGone()
    const data = await response.json()
    if (!data?.success) throw new Error(data?.error?.message || 'screen share')
    return data.result as T
  }

  private negotiating() {
    return this.pc !== null && !this.connected
  }

  private poll = async () => {
    if (this.stopped) return
    try {
      const result = await this.call<PollResult>('GET', `&after=${this.after}`)
      if (result.state === 'ended') {
        this.finish()
        return
      }
      for (const signal of result.signals || []) {
        this.after = Math.max(this.after, signal.id)
        await this.take(signal)
      }
      if (!this.stopped)
        publish({
          ...current,
          status: result.state === 'viewing' ? 'viewing' : 'waiting',
          viewer: result.viewer || null,
        })
    } catch (reason) {
      if (reason instanceof ShareGone) {
        this.finish()
        return
      }
      // The line dropped for a moment: asked again.
    }
    // The customer may be showing another tab or program: the panel's tab is then hidden, and still asks on time.
    if (!this.stopped)
      this.cancelPoll = steadyTimeout(
        this.poll,
        this.negotiating() ? NEGOTIATING_POLL_MS : this.start.pollMs
      )
  }

  private async take(signal: Signal) {
    let data: RTCSessionDescriptionInit & RTCIceCandidateInit
    try {
      data = JSON.parse(signal.payload)
    } catch {
      return
    }
    if (signal.kind === 'viewer') {
      await this.offer()
    } else if (signal.kind === 'answer' && this.pc) {
      await this.pc.setRemoteDescription(data)
      for (const candidate of this.queued.splice(0))
        await this.pc.addIceCandidate(candidate).catch(() => undefined)
    } else if (signal.kind === 'candidate') {
      if (this.pc?.remoteDescription) await this.pc.addIceCandidate(data).catch(() => undefined)
      else this.queued.push(data)
    } else if (signal.kind === 'bye') {
      this.closePeer()
    }
  }

  /** A colleague came: a new connection to them, the screen sharp (a sharp picture before a smooth one). */
  private async offer() {
    this.closePeer()
    const pc = new RTCPeerConnection({iceServers: this.start.iceServers})
    this.pc = pc
    this.queued = []
    this.connected = false
    this.stream.getVideoTracks().forEach((track) => {
      try {
        track.contentHint = 'detail'
      } catch {
        // An older browser keeps its own choice.
      }
      pc.addTrack(track, this.stream)
    })
    pc.onicecandidate = (event) => {
      if (event.candidate)
        this.call('POST', '&action=signal', {
          kind: 'candidate',
          payload: JSON.stringify(event.candidate.toJSON()),
        }).catch(() => undefined)
    }
    pc.onconnectionstatechange = () => {
      this.connected = pc.connectionState === 'connected'
    }
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    await Promise.all(
      pc.getSenders().map(async (sender) => {
        const parameters = sender.getParameters() as Tunable
        if (!parameters.encodings?.length) parameters.encodings = [{}]
        parameters.encodings[0].maxBitrate = MAX_BITRATE
        parameters.degradationPreference = 'maintain-resolution'
        await sender.setParameters(parameters).catch(() => undefined)
      })
    )
    await this.call('POST', '&action=signal', {
      kind: 'offer',
      payload: JSON.stringify({type: offer.type, sdp: offer.sdp}),
    })
  }

  private closePeer() {
    if (this.pc) this.pc.close()
    this.pc = null
    this.connected = false
  }

  private finish() {
    if (this.stopped) return
    this.stopped = true
    this.cancelPoll()
    this.closePeer()
    this.stream.getTracks().forEach((track) => {
      track.removeEventListener('ended', this.onEnded)
      track.stop()
    })
    window.removeEventListener('pagehide', this.onPageHide)
    if (session === this) session = null
    publish({status: 'ended', ticketNumber: this.ticketNumber})
    window.clearTimeout(endedTimer)
    endedTimer = window.setTimeout(() => {
      if (current.status === 'ended') publish({status: 'idle'})
    }, 5000)
  }

  /** The browser's own «Stop sharing». */
  private onEnded = () => this.stop()

  /** The page closing: told with a beacon, which carries no header (the token goes in the address). */
  private onPageHide = () => {
    if (this.stopped) return
    if (navigator.sendBeacon)
      navigator.sendBeacon(this.url(`&action=end&token=${this.start.token}`))
    this.finish()
  }
}

/**
 * The customer chooses a screen, a window or a tab (the browser asks), and the share of the ticket begins; the ticket's
 * owner is told. A refusal of the browser's question starts nothing.
 * From a panel page it goes to the capture window (60.11.580), where the customer chooses the screen and the share
 * outlives the page; where the browser opens no window, it runs in the page itself.
 */
export const startScreenShare = async (ticketNumber: string) => {
  if (session || localStarting || (remote && screenShareBusy(remote.state))) return
  window.clearTimeout(endedTimer)
  if (!inCaptureWindow() && openCaptureWindow({kind: 'share', ticketNumber})) {
    publish({status: 'choosing', ticketNumber})
    // A window that never answers (closed at once, or its page failed) lets the request go.
    window.clearTimeout(waitTimer)
    waitTimer = window.setTimeout(() => {
      if (!remote && !session && current.status === 'choosing') publish({status: 'idle'})
    }, CAPTURE_WINDOW_WAIT_MS)
    return
  }
  // The capture window stays to choose again after a refusal; a page goes back to nothing.
  const refused: ScreenShareState = inCaptureWindow()
    ? {status: 'choosing', ticketNumber}
    : {status: 'idle'}
  localStarting = true
  publish({status: 'starting', ticketNumber})
  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: {frameRate: {ideal: 15, max: 20}},
      audio: false,
    })
  } catch {
    localStarting = false
    publish(refused)
    return
  }
  try {
    const start = await startTicketScreenShare(ticketNumber)
    session = new ScreenShareSession(start, stream, ticketNumber)
    localStarting = false
    session.begin()
  } catch (reason) {
    localStarting = false
    stream.getTracks().forEach((track) => track.stop())
    publish(refused)
    throw reason
  }
}

/** «توقف نمایش»: this page's share, the capture window's, or a choice not made yet. */
export const stopScreenShare = () => {
  if (session) session.stop()
  else if (remote) remote.stop()
  else if (current.status === 'choosing') publish({status: 'idle'})
}
